/* ==========================================================================
   product.js — PDP behaviors (SPEC §11, §14.3, §20, §28; DECISIONS D-008/D-012).
   Everything enhances server-rendered markup: the product form posts to
   /cart/add without JS; the gallery is a scroll-snap list; tabs fall back
   to visible stacked panels.
   ========================================================================== */

import { cart, toast, announce } from './global.js';

/* Idempotent registration (see global.js): guards against this module being
   evaluated more than once, which would otherwise throw on a duplicate define
   and abort the rest of the file. */
function defineElement(name, ctor, options) {
  if (!customElements.get(name)) customElements.define(name, ctor, options);
}

/* --------------------------------------------------------------------------
   <product-page>: size selection + AJAX ATC + sticky bar arbitration
   -------------------------------------------------------------------------- */

defineElement(
  'product-page',
  class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.atc = this.querySelector('[data-atc]');
      this.stickyBar = this.querySelector('[data-sticky-atc]');
      this.buybox = this.querySelector('[data-buybox]');

      /* Variant picker (v7.0): colour/size radios resolve a live variant —
         the hidden id, price, ATC state, URL and sticky bar all follow the
         selection; option values with no available combo are greyed out */
      this.variants = this.readJSON('[data-variant-json]') || [];
      this.priceEl = this.querySelector('[data-price]');
      this.stickyPriceEl = this.querySelector('[data-sticky-price]');
      this.optionRows = [...this.querySelectorAll('[data-optrow]')];
      this.atcDefault = this.atc ? this.atc.textContent.trim() : '';
      if (this.variants.length && this.optionRows.length) {
        this.querySelectorAll('[data-option-input]').forEach((input) =>
          input.addEventListener('change', () => this.onVariantChange())
        );
        this.reflectAvailability();
      }

      /* AJAX add-to-cart with success morph (§14.3) */
      this.form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const label = this.atc.textContent;
        this.atc.setAttribute('aria-busy', 'true');
        try {
          await cart.add([{ id: Number(this.idInput.value), quantity: 1 }]);
          this.atc.removeAttribute('aria-busy');
          this.atc.textContent = this.atc.dataset.addedLabel || '✓';
          announce(this.atc.dataset.addedAnnounce || '');
          setTimeout(() => (this.atc.textContent = label), 1200);
        } catch (err) {
          this.atc.removeAttribute('aria-busy');
          if (err.status === 422) toast(err.message);
          else this.form.submit(); // degrade to server flow
        }
      });

      /* Sticky ATC bar: appears when the buy box scrolls out (§11.4);
         flags <body> so the header never re-reveals over it (D-012) */
      if (this.stickyBar && this.buybox) {
        this.stickyBar.hidden = false;
        new IntersectionObserver(
          ([entry]) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.stickyBar.classList.toggle('is-visible', show);
            document.body.toggleAttribute('data-atc-bar-active', show);
          },
          { threshold: 0 }
        ).observe(this.buybox);

        this.stickyBar.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', () => {
          const selected = this.querySelector('[data-size-selector] input:checked');
          if (!selected && this.querySelector('[data-size-selector]')) {
            this.buybox.scrollIntoView({ behavior: 'smooth', block: 'center' });
            toast(this.stickyBar.dataset.selectSize || '');
            return;
          }
          this.form.requestSubmit();
        });
      }
    }

    disconnectedCallback() {
      document.body.removeAttribute('data-atc-bar-active');
    }

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch {
        return null;
      }
    }

    /* Read the currently-checked value of each option row; also echoes the
       chosen value into the label beside the option name. */
    currentOptions() {
      const opts = [];
      this.optionRows.forEach((row) => {
        const i = Number(row.dataset.position) - 1;
        const checked = row.querySelector('[data-option-input]:checked');
        opts[i] = checked ? checked.value : null;
        const label = row.querySelector('[data-option-value]');
        if (label && checked) label.textContent = checked.value;
      });
      return opts;
    }

    onVariantChange() {
      const opts = this.currentOptions();
      this.reflectAvailability(opts);

      const variant = this.variants.find((v) => opts.every((o, i) => v.options[i] === o));
      if (!variant) {
        if (this.atc) {
          this.atc.disabled = true;
          this.atc.textContent = this.atc.dataset.soldoutLabel || this.atcDefault;
        }
        return;
      }

      this.idInput.value = variant.id;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);

      if (this.priceEl) {
        this.priceEl.innerHTML = variant.on_sale
          ? `<s class="pdp__price-was">${variant.compare_at}</s><span class="pdp__price-sale">${variant.price}</span>`
          : variant.price;
      }
      if (this.stickyPriceEl) this.stickyPriceEl.textContent = variant.price;
      if (this.atc) {
        this.atc.disabled = !variant.available;
        this.atc.textContent = variant.available
          ? this.atcDefault
          : this.atc.dataset.soldoutLabel || this.atcDefault;
      }
    }

    /* Grey out any option value that has no available variant given the
       other currently-selected options (Represent/luxury grammar). */
    reflectAvailability(opts) {
      const current = opts || this.currentOptions();
      this.optionRows.forEach((row) => {
        const pos = Number(row.dataset.position) - 1;
        row.querySelectorAll('[data-option-input]').forEach((input) => {
          const probe = current.slice();
          probe[pos] = input.value;
          const reachable = this.variants.some(
            (v) => v.available && probe.every((o, i) => o == null || v.options[i] === o)
          );
          const cell = input.closest('.pdp__size, .pdp__swatch-cell');
          if (cell) cell.classList.toggle('pdp__size--unavailable', !reachable);
        });
      });
    }
  }
);

/* --------------------------------------------------------------------------
   <delivery-estimate>: computed date range (D-008), fr-FR, weekends skipped
   for the dispatch bound. Server fallback text stays if JS never runs.
   -------------------------------------------------------------------------- */

defineElement(
  'delivery-estimate',
  class extends HTMLElement {
    connectedCallback() {
      const addBusinessDays = (start, days) => {
        const d = new Date(start);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) added += 1;
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
      const min = addBusinessDays(Date.now(), Number(this.dataset.min || 8) + 2);
      const max = addBusinessDays(Date.now(), Number(this.dataset.max || 12) + 4);
      const template = this.dataset.template || 'Livraison estimée : [start] – [end]';
      this.textContent = template.replace('[start]', fmt.format(min)).replace('[end]', fmt.format(max));
    }
  }
);

/* --------------------------------------------------------------------------
   <media-gallery>: slide tracking, thumbs, arrows, lightbox (§11.2, §20)
   -------------------------------------------------------------------------- */

defineElement(
  'media-gallery',
  class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.total = Number(this.dataset.total || this.slides.length);
      this.index = 0;
      if (!this.track || this.slides.length < 2) {
        this.initLightbox();
        return;
      }

      /* Track scroll position → current index (mobile swipe) */
      let scrollTimer;
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const i = Math.round(this.track.scrollLeft / this.slides[0].offsetWidth);
            this.setIndex(Math.min(i, this.total - 1), { scroll: false });
          }, 80);
        },
        { passive: true }
      );

      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.setIndex(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.setIndex(this.index + 1));
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.setIndex(Number(thumb.dataset.index)))
      );

      this.initLightbox();
    }

    setIndex(next, { scroll = true } = {}) {
      this.index = (next + this.total) % this.total;
      if (scroll) {
        this.slides[this.index]?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === this.index);
        if (i === this.index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.counter) {
        const template = this.counter.dataset.template || this.counter.textContent;
        this.counter.dataset.template = template;
        this.counter.textContent = template
          .replace(/\d+(?= \D+ \d+)/, this.index + 1)
          .replace(/\d+$/, this.total);
      }
    }

    initLightbox() {
      const lightbox = this.querySelector('[data-lightbox]');
      if (!lightbox) return;
      this.querySelectorAll('[data-lightbox-open]').forEach((btn) =>
        btn.addEventListener('click', () => {
          lightbox.showModal();
          lightbox
            .querySelector(`[data-lightbox-slide][data-index="${btn.dataset.index}"]`)
            ?.scrollIntoView({ behavior: 'auto', block: 'start' });
        })
      );
      lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => lightbox.close());
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox) lightbox.close();
      });
    }
  }
);

/* --------------------------------------------------------------------------
   <size-guide-tabs>: WAI-ARIA tabs, arrow-key navigation (§28)
   -------------------------------------------------------------------------- */

defineElement(
  'size-guide-tabs',
  class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
          if (!dir) return;
          e.preventDefault();
          const next = (i + dir + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
    }
  }
);
